/**
 * Guides « réserver une voiture au Maroc depuis l’Europe » — intention voyage claire.
 * Pas de location locale en Europe : uniquement mobilité au Maroc via RAKB.
 */

import type { GuidePageData } from './guide-page-types';

const FAQ_BASE = (
  cityFr: string,
  cityEn: string
): NonNullable<GuidePageData['faqItems']> => [
  {
    qFr: 'Puis-je réserver une voiture au Maroc avant de quitter mon pays ?',
    aFr: `Oui. Sur RAKB vous choisissez vos dates, comparez les agences partenaires au Maroc et réservez en ligne. La prise en charge a lieu au Maroc uniquement (aéroport ${cityFr}, centre-ville ou selon les options du loueur) : RAKB n’est pas un loueur en Europe, c’est une marketplace de location au Maroc.`,
    qEn: 'Can I book a car in Morocco before leaving my country?',
    aEn: `Yes. On RAKB you pick dates, compare partner agencies in Morocco, and book online. Pickup happens in Morocco only (airport ${cityEn}, downtown, or per the rental company’s options). RAKB is a Morocco car rental marketplace—not a European rental operator.`,
  },
  {
    qFr: 'Mon permis français / européen est-il accepté au Maroc ?',
    aFr: 'Les permis de conduire délivrés dans l’Union européenne, le Royaume-Uni et de nombreux autres pays sont en principe acceptés pour une location de courte durée au Maroc. Vérifiez la validité, l’ancienneté et les éventuelles conditions « jeune conducteur » avec l’agence au moment de la réservation.',
    qEn: 'Is my EU / UK licence accepted in Morocco?',
    aEn: 'Licences issued in the EU, UK, and many other countries are generally accepted for short-term rentals in Morocco. Check validity, minimum years held, and any young-driver rules with the agency when you book.',
  },
  {
    qFr: 'Le paiement en ligne est-il sécurisé ?',
    aFr: 'RAKB propose un parcours de réservation en ligne avec des partenaires identifiés. Les modalités de caution et d’assurance sont affichées par l’agence : lisez-les avant de confirmer pour éviter toute surprise.',
    qEn: 'Is online payment secure?',
    aEn: 'RAKB offers an online booking flow with identified partners. Deposit and insurance terms are shown by each agency: read them before confirming.',
  },
  {
    qFr: 'Puis-je récupérer la voiture à l’aéroport à mon arrivée ?',
    aFr: 'Très souvent oui : Marrakech-Menara, Mohammed V (Casablanca), Rabat-Salé, Tanger Ibn Battouta, etc. Indiquez votre numéro de vol et l’heure d’arrivée pour faciliter la remise des clés.',
    qEn: 'Can I pick up the car at the airport on arrival?',
    aEn: 'Often yes: Menara, Mohammed V, Rabat-Salé, Tangier Ibn Battouta, etc. Share your flight number and arrival time to smooth key handover.',
  },
  {
    qFr: 'Permis international (IDP) : quand est-ce utile ?',
    aFr: 'Pour un permis UE ou UK au format carte en alphabet latin, un IDP est souvent inutile pour une courte location touristique, mais ce n’est pas une règle absolue : certains loueurs peuvent le demander ou exiger une traduction si le document est illisible. En cas de doute, écrivez à l’agence avant de payer.',
    qEn: 'International Driving Permit (IDP): when is it useful?',
    aEn: 'For a plastic EU/UK card licence in Latin script, an IDP is often unnecessary for short tourist rentals, but it is not universal—some agencies may still ask for an IDP or a translation if the licence is hard to read. When unsure, message the agency before you pay.',
  },
  {
    qFr: 'L’assurance affichée couvre-t-elle tout sans franchise ?',
    aFr: 'Non : la base couvre généralement la responsabilité civile ; vol, bris de glace ou dommages peuvent être soumis à franchise selon le contrat du loueur. Les montants et options (super CDW, réduction de franchise) sont sur la fiche véhicule : lisez-les avant de confirmer.',
    qEn: 'Does the listed insurance cover everything with zero excess?',
    aEn: 'Usually not—base cover is often third-party liability, while theft, glass, or damage may carry an excess per the agency contract. Amounts and add-ons (super CDW, excess reduction) are on the listing: read them before you confirm.',
  },
  {
    qFr: 'RAKB est-il une agence de location en Europe ?',
    aFr: 'Non. RAKB est une plateforme qui met en relation voyageurs et loueurs partenaires au Maroc. Vous comparez et réservez pour une utilisation au Maroc ; le contrat et la caution s’appliquent selon les conditions marocaines du loueur.',
    qEn: 'Is RAKB a rental company based in Europe?',
    aEn: 'No. RAKB is a marketplace connecting travelers with partner rental companies in Morocco. You compare and book for use in Morocco; the contract and deposit follow the Moroccan partner’s terms.',
  },
];

export const EUROPE_MOROCCO_GUIDES: GuidePageData[] = [
  {
    slug: 'location-voiture-maroc-depuis-france',
    slugEn: 'rent-car-morocco-from-france',
    title: 'Location voiture Maroc depuis la France — Réserver avant le vol | RAKB',
    titleEn: 'Rent a car in Morocco from France — Book before you fly | RAKB',
    metaDescription:
      'Réservez votre location de voiture au Maroc depuis la France : documents, aéroports, assurance, paiement. RAKB compare les agences partenaires.',
    metaDescriptionEn:
      'Book car rental in Morocco from France: documents, airports, insurance, payment. RAKB compares partner agencies.',
    h1: 'Louer une voiture au Maroc depuis la France',
    h1En: 'Rent a car in Morocco while you are in France',
    date: '2026-05-12',
    readTime: '11 min',
    keywords:
      'location voiture maroc depuis france, louer voiture maroc depuis france, réserver voiture maroc paris, location marrakech depuis france',
    keywordsEn:
      'rent car morocco from france, book morocco car rental from france, morocco car hire french travelers',
    sections: [
      {
        heading: 'Une intention claire : la voiture roule au Maroc, pas en France',
        text: 'Ce guide s’adresse aux résidents ou visiteurs en France qui préparent un séjour au Maroc et souhaitent réserver une voiture pour Casablanca, Marrakech, Rabat, Tanger ou une autre ville marocaine. RAKB est une marketplace : les véhicules sont loués et utilisés au Maroc auprès d’agences partenaires, avec des tarifs affichés de manière transparente.',
      },
      {
        heading: 'Pourquoi réserver avant le départ ?',
        text: 'Les vols vers Marrakech, Casablanca ou Tanger sont très demandés en haute saison. Réserver votre véhicule en avance sécurise le segment « mobilité » de votre voyage : catégories disponibles (citadine, SUV, automatique), options et créneaux de livraison aéroport. Vous comparez plusieurs agences sur une seule interface au lieu de multiplier les e-mails.',
      },
      {
        heading: 'Arrivée typique : Paris ou province → hub marocain',
        text: 'Les voyageurs au départ de la France atterrissent surtout à Marrakech-Menara, Mohammed V (Casablanca) ou, pour le nord, Tanger Ibn Battouta. Précisez votre aéroport d’arrivée et votre numéro de vol lors de la réservation : les partenaires RAKB habitués au trafic international organisent la remise des clés au terminal ou à proximité selon leur procédure.',
      },
      {
        heading: 'Documents et assurance : ce qu’il faut avoir en tête',
        text: 'Préparez permis de conduire, passeport et carte bancaire pour la caution. Les franchises et assurances varient selon l’agence : le détail figure sur la fiche véhicule. Si vous prévoyez l’Atlas ou des routes de montagne, choisissez une catégorie adaptée (souvent SUV) et anticipez les conditions météo en hiver.',
      },
      {
        heading: 'Paiement et réassurance client',
        text: 'RAKB concentre l’offre sur rakb.ma : vous voyez les prix, les options et le contact de l’agence partenaire. En cas de question (changement de vol, retard), contactez tôt le loueur via les coordonnées fournies après réservation pour ajuster le créneau de remise.',
      },
    ],
    sectionsEn: [
      {
        heading: 'Clear intent: the car is used in Morocco, not in France',
        text: 'This guide is for people in France planning a trip to Morocco who want a rental car in Casablanca, Marrakech, Rabat, Tangier, or another Moroccan city. RAKB is a marketplace: vehicles are rented and driven in Morocco with partner agencies and transparent pricing.',
      },
      {
        heading: 'Why book before you fly?',
        text: 'Flights to Marrakech, Casablanca, or Tangier are busy in peak season. Booking early secures the mobility leg of your trip: vehicle categories, options, and airport delivery slots. You compare several agencies in one place.',
      },
      {
        heading: 'Typical routing: France → Moroccan hub',
        text: 'Travelers from France often land at Menara, Mohammed V, or Tangier Ibn Battouta. Share your arrival airport and flight number when booking so partner agencies can coordinate key handover.',
      },
      {
        heading: 'Documents and insurance',
        text: 'Bring licence, passport, and a card for the security deposit. Deductibles and coverage vary by agency and are shown on the listing. For mountain routes, pick a suitable category (often SUV) and check winter conditions.',
      },
      {
        heading: 'Payment and support',
        text: 'RAKB surfaces offers on rakb.ma with prices and partner contact details. If your flight changes, reach out early to adjust pickup time.',
      },
    ],
    faqItems: FAQ_BASE('Marrakech, Casablanca, Tanger', 'Marrakech, Casablanca, Tangier'),
  },
  {
    slug: 'location-voiture-maroc-depuis-belgique',
    slugEn: 'rent-car-morocco-from-belgium',
    title: 'Location voiture Maroc depuis la Belgique | RAKB',
    titleEn: 'Rent a car in Morocco from Belgium | RAKB',
    metaDescription:
      'Réservez une voiture au Maroc depuis la Belgique (Bruxelles, Charleroi) : vols, aéroports marocains, documents. Comparaison d’agences sur RAKB.',
    metaDescriptionEn:
      'Book a car in Morocco from Belgium (Brussels, Charleroi): flights, Moroccan airports, documents. Compare agencies on RAKB.',
    h1: 'Louer une voiture au Maroc depuis la Belgique',
    h1En: 'Rent a car in Morocco from Belgium',
    date: '2026-05-12',
    readTime: '10 min',
    keywords: 'location voiture maroc depuis belgique, louer voiture maroc bruxelles, rakb belgique maroc',
    keywordsEn: 'rent car morocco from belgium, morocco car rental from brussels',
    sections: [
      {
        heading: 'Bruxelles et Charleroi : hubs pratiques vers le Maroc',
        text: 'Les liaisons vers Casablanca, Marrakech ou Nador sont fréquentes. Anticipez la saison (vacances scolaires belges et marocaines) pour les tarifs de vol et de location. RAKB vous permet de verrouiller la voiture au sol marocain pendant que vous finalisez l’avion.',
      },
      {
        heading: 'Marché commun et permis',
        text: 'Les permis délivrés en Belgique sont en général reconnus pour la location courte durée au Maroc, sous réserve des conditions d’âge et d’ancienneté du loueur. Gardez une copie PDF de votre réservation et du contrat sur votre téléphone.',
      },
      {
        heading: 'Après l’atterrissage',
        text: 'Privilégiez une prise en charge aéroport si vous arrivez tard ou avec des bagages lourds. Vérifiez le niveau de carburant et l’état des lieux avec l’agence avant de quitter le parking.',
      },
    ],
    sectionsEn: [
      {
        heading: 'Brussels and Charleroi to Morocco',
        text: 'Flights to Casablanca, Marrakech, or Nador are common. Peak holiday periods affect both flights and rental prices. RAKB helps you lock in ground transport in Morocco.',
      },
      {
        heading: 'Licence and paperwork',
        text: 'Belgian licences are generally accepted for short rentals in Morocco, subject to agency rules on age and experience. Keep PDF copies of your booking on your phone.',
      },
      {
        heading: 'After landing',
        text: 'Airport pickup is convenient for late arrivals or heavy luggage. Check fuel level and the vehicle condition report before leaving the lot.',
      },
    ],
    faqItems: FAQ_BASE('Casablanca, Marrakech', 'Casablanca, Marrakech'),
  },
  {
    slug: 'location-voiture-maroc-depuis-pays-bas',
    slugEn: 'rent-car-morocco-from-netherlands',
    title: 'Location voiture Maroc depuis les Pays-Bas | RAKB',
    titleEn: 'Rent a car in Morocco from the Netherlands | RAKB',
    metaDescription:
      'Voyage Pays-Bas → Maroc : réservez votre voiture à Marrakech, Agadir ou Casablanca. Conseils vols, permis, assurance. RAKB.',
    metaDescriptionEn:
      'Netherlands to Morocco travel: book your car in Marrakech, Agadir, or Casablanca. Flights, licence, insurance tips. RAKB.',
    h1: 'Louer une voiture au Maroc depuis les Pays-Bas',
    h1En: 'Rent a car in Morocco from the Netherlands',
    date: '2026-05-12',
    readTime: '10 min',
    keywords: 'location voiture maroc depuis pays-bas, rent car morocco from amsterdam',
    keywordsEn: 'car rental morocco from netherlands, morocco hire from amsterdam',
    sections: [
      {
        heading: 'Schiphol et Eindhoven vers le Maroc',
        text: 'Les voyageurs néerlandais rejoignent souvent Marrakech ou Agadir pour le soleil, ou Casablanca pour l’affaires. Une voiture au Maroc simplifie les transferts longue distance (Agadir–Essaouira, Marrakech–Atlas) sans dépendre uniquement des taxis interurbains.',
      },
      {
        heading: 'Permis et conduite',
        text: 'Le permis néerlandais (carte plastique UE) est habituellement accepté. Prévoyez une carte bancaire internationale pour la caution. Les péages autoroutiers se règlent en dirhams : gardez des espèces pour les premiers trajets.',
      },
      {
        heading: 'Choix du véhicule',
        text: 'Pour un road trip côtier, une berline ou compacte suffit souvent. Pour le Haut Atlas ou des pistes secondaires humides, un SUV est plus confortable. Filtrez sur RAKB par catégorie et boîte de vitesses.',
      },
    ],
    sectionsEn: [
      {
        heading: 'Schiphol and Eindhoven to Morocco',
        text: 'Dutch travelers often fly to Marrakech or Agadir for sun, or Casablanca for business. A rental car simplifies longer legs without relying only on intercity taxis.',
      },
      {
        heading: 'Licence and driving',
        text: 'Dutch EU-format licences are usually accepted. Bring an international card for deposits. Highway tolls are paid in MAD—keep some cash for first trips.',
      },
      {
        heading: 'Vehicle choice',
        text: 'Coastal trips often suit a compact or sedan. For the High Atlas or rougher secondary roads, consider an SUV. Filter on RAKB by category and transmission.',
      },
    ],
    faqItems: FAQ_BASE('Marrakech, Agadir, Casablanca', 'Marrakech, Agadir, Casablanca'),
  },
  {
    slug: 'location-voiture-maroc-depuis-allemagne',
    slugEn: 'rent-car-morocco-from-germany',
    title: 'Location voiture Maroc depuis l’Allemagne | RAKB',
    titleEn: 'Rent a car in Morocco from Germany | RAKB',
    metaDescription:
      'Réserver une voiture au Maroc depuis l’Allemagne : vols directs ou via hubs, permis, assurance. RAKB compare les loueurs au Maroc.',
    metaDescriptionEn:
      'Book a car in Morocco from Germany: direct or hub flights, licence, insurance. RAKB compares rental companies in Morocco.',
    h1: 'Louer une voiture au Maroc depuis l’Allemagne',
    h1En: 'Rent a car in Morocco from Germany',
    date: '2026-05-12',
    readTime: '10 min',
    keywords: 'location voiture maroc depuis allemagne, mietwagen marokko aus deutschland buchen rakb',
    keywordsEn: 'rent car morocco from germany, morocco car rental german travelers',
    sections: [
      {
        heading: 'Vols depuis l’Allemagne',
        text: 'Les connexions vers Casablanca ou Marrakech existent depuis plusieurs grandes villes allemandes, parfois avec une escale. Moins vous laissez la mobilité locale au hasard, plus le début de séjour est fluide : réservez la voiture marocaine en même temps que vous figez les dates d’hôtel.',
      },
      {
        heading: 'Permis et traduction',
        text: 'Le permis allemand est en principe reconnu pour la location touristique courte. Si votre permis n’est pas en latin, une traduction peut être demandée par certains loueurs : vérifiez sur la fiche réservation.',
      },
      {
        heading: 'Culture de la route',
        text: 'Le Maroc combine autoroutes modernes et circulation urbaine dense. Anticipez les heures de pointe à Casablanca ou Rabat. Le GPS fonctionne bien sur les axes principaux ; téléchargez une carte hors ligne pour le désert ou les cols.',
      },
    ],
    sectionsEn: [
      {
        heading: 'Flights from Germany',
        text: 'Connections to Casablanca or Marrakech exist from several German cities, sometimes with a stop. Booking Moroccan ground transport early makes the start of your trip smoother.',
      },
      {
        heading: 'Licence',
        text: 'German licences are generally accepted for short tourist rentals. If your licence is not Latin script, some agencies may ask for a translation—check the listing.',
      },
      {
        heading: 'On the road',
        text: 'Morocco mixes modern highways with dense city traffic. Expect rush hours in Casablanca or Rabat. Offline maps help in remote areas.',
      },
    ],
    faqItems: FAQ_BASE('Casablanca, Marrakech', 'Casablanca, Marrakech'),
  },
  {
    slug: 'location-voiture-maroc-depuis-espagne',
    slugEn: 'rent-car-morocco-from-spain',
    title: 'Location voiture Maroc depuis l’Espagne (vol ou ferry) | RAKB',
    titleEn: 'Rent a car in Morocco from Spain (flight or ferry) | RAKB',
    metaDescription:
      'Court trajet Europe–Maroc : réserver une voiture à Tanger, Nador ou Casablanca depuis l’Espagne. Conseils ferry, vols, documents. RAKB.',
    metaDescriptionEn:
      'Short Europe–Morocco hop: book a car in Tangier, Nador, or Casablanca from Spain. Ferry or flight tips. RAKB.',
    h1: 'Louer une voiture au Maroc depuis l’Espagne',
    h1En: 'Rent a car in Morocco from Spain',
    date: '2026-05-12',
    readTime: '10 min',
    keywords: 'location voiture maroc depuis espagne, alquiler coche marruecos desde españa rakb',
    keywordsEn: 'rent car morocco from spain, spain to morocco car rental',
    sections: [
      {
        heading: 'Proximité géographique, même logique de réservation',
        text: 'Beaucoup de voyageurs arrivent par vol court depuis Madrid, Barcelone ou Malaga, ou par ferry vers Tanger Med / Tanger Ville. Dans tous les cas, le véhicule loué via RAKB circule au Maroc : choisissez la ville de prise en charge (Tanger, Nador, Casablanca…) selon votre point d’entrée.',
      },
      {
        heading: 'Tanger comme porte d’entrée',
        text: 'Tanger combine ferry, TGV et vols. Si vous atterrissez ou débarquez tard, une livraison aéroport ou port selon disponibilité partenaire évite la course aux taxis avec bagages.',
      },
      {
        heading: 'Documents',
        text: 'Permis espagnol UE, passeport ou CNI selon les règles en vigueur pour votre nationalité. La caution reste généralement une empreinte bancaire : renseignez-vous sur les plafonds de votre banque avant le départ.',
      },
    ],
    sectionsEn: [
      {
        heading: 'Close to Spain, same booking logic',
        text: 'Many travelers arrive on short flights from Madrid, Barcelona, or Malaga, or by ferry to Tangier Med / Tangier City. The RAKB rental is always used in Morocco—pick the pickup city to match your entry point.',
      },
      {
        heading: 'Tangier as a gateway',
        text: 'Tangier combines ferry, rail, and flights. Late arrivals benefit from scheduled airport/port handovers when partners offer them.',
      },
      {
        heading: 'Documents',
        text: 'EU Spanish licence plus passport or ID as required. Deposits are usually card holds—check limits with your bank before travel.',
      },
    ],
    faqItems: FAQ_BASE('Tanger, Nador, Casablanca', 'Tangier, Nador, Casablanca'),
  },
  {
    slug: 'location-voiture-maroc-depuis-royaume-uni',
    slugEn: 'rent-car-morocco-from-uk',
    title: 'Location voiture Maroc depuis le Royaume-Uni | RAKB',
    titleEn: 'Rent a car in Morocco from the UK | RAKB',
    metaDescription:
      'Voyageurs UK : réservez une voiture au Maroc (Marrakech, Agadir, Casablanca). Permis, assurance, vols directs ou via l’Europe. RAKB.',
    metaDescriptionEn:
      'UK travelers: book a car in Morocco (Marrakech, Agadir, Casablanca). Licence, insurance, direct or via-Europe flights. RAKB.',
    h1: 'Louer une voiture au Maroc depuis le Royaume-Uni',
    h1En: 'Rent a car in Morocco from the UK',
    date: '2026-05-12',
    readTime: '11 min',
    keywords: 'location voiture maroc depuis uk, rent car morocco from uk british license',
    keywordsEn: 'rent car morocco from uk, morocco car hire british passport',
    sections: [
      {
        heading: 'Vol direct ou correspondance',
        text: 'Les voyageurs britanniques rejoignent souvent Marrakech ou Agadir, parfois via un hub européen. Réserver la voiture côté marocain sépare le billet d’avion (soumis aux compagnies) de la mobilité (comparée sur RAKB).',
      },
      {
        heading: 'Permis du Royaume-Uni',
        text: 'Les loueurs marocains accueillent régulièrement des clients internationaux. Confirmez avec l’agence que votre permis UK en cours de validité est accepté pour la catégorie réservée et pour la durée du séjour.',
      },
      {
        heading: 'Conduite à droite',
        text: 'Comme au Royaume-Uni la circulation est à gauche, mais au Maroc à droite : accordez-vous une courte phase d’adaptation en sortant de l’aéroport. Les autoroutes sont larges et bien balisées.',
      },
      {
        heading: 'Assurance et carte bancaire',
        text: 'Vérifiez si votre carte premium inclut une couverture location à l’étranger ; elle ne remplace pas l’assurance de base du loueur mais peut compléter la couverture. La franchise reste contractuelle avec l’agence.',
      },
    ],
    sectionsEn: [
      {
        heading: 'Direct or connecting flights',
        text: 'UK travelers often fly to Marrakech or Agadir, sometimes via a European hub. Booking Moroccan rental separately keeps flight changes from blocking your ground transport planning.',
      },
      {
        heading: 'UK licences',
        text: 'Moroccan agencies regularly serve international clients. Confirm your valid UK licence is accepted for the booked category and trip length.',
      },
      {
        heading: 'Right-hand traffic',
        text: 'Morocco drives on the right—allow a short adaptation after pickup. Highways are wide and well signed.',
      },
      {
        heading: 'Insurance and cards',
        text: 'Check if your premium card adds rental coverage abroad; it complements but does not replace the agency’s base insurance. Deductible rules remain with the rental contract.',
      },
    ],
    faqItems: FAQ_BASE('Marrakech, Agadir, Casablanca', 'Marrakech, Agadir, Casablanca'),
  },
];
